import { Button, Menu, MenuRadioGroup, MenuRadioItem } from '@au/ui';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { type AppLocale, isAppLocale } from './locale-storage';
import styles from './language-select.module.css';

const LOCALES: readonly AppLocale[] = ['es', 'en'];

/**
 * El nombre de cada idioma en su propio idioma («Español», «English»), sin banderas. `Intl` lo da en
 * minúscula en español («español»); en un menú va con mayúscula inicial. No cambian: se calculan una
 * vez al cargar el módulo.
 */
const NATIVE_NAMES = Object.fromEntries(
  LOCALES.map((locale) => {
    const name = new Intl.DisplayNames([locale], { type: 'language' }).of(locale) ?? locale;
    return [locale, name.charAt(0).toLocaleUpperCase(locale) + name.slice(1)];
  }),
) as Record<AppLocale, string>;

interface LanguageSelectProps {
  value: AppLocale;
  onChange: (locale: AppLocale) => void;
  /** La última elección no se pudo guardar: se avisa junto al control. */
  error: boolean;
}

/**
 * Un `Button` de `@au` de solo ícono, par del `ThemeToggle`, que muestra el código del idioma activo
 * («ES»/«EN») y abre un `Menu` de `@au` con un radio por idioma. Cada opción lleva `lang` para que se
 * anuncie en su idioma (la razón por la que no se usa el `Select` de la capa). El código va
 * `aria-hidden`: el nombre accesible dice el idioma completo. Elegir cierra el menú (`closeOnClick`;
 * en Base UI un radio item lo deja abierto por default). Presentacional: el estado lo tiene
 * `useUserLocale()`.
 */
export function LanguageSelect({ value, onChange, error }: LanguageSelectProps) {
  const { _ } = useLingui();
  const current = NATIVE_NAMES[value];
  const label = _(msg`Idioma: ${current}`);

  return (
    <div className={styles.languageSelect}>
      <Menu
        align="end"
        trigger={
          <Button icon variant="ghost" tone="info" size="sm" aria-label={label}>
            <span aria-hidden className={styles.code}>
              {value.toUpperCase()}
            </span>
          </Button>
        }
      >
        <MenuRadioGroup
          value={value}
          onValueChange={(next: unknown) => {
            if (isAppLocale(next) && next !== value) onChange(next);
          }}
        >
          {LOCALES.map((locale) => (
            <MenuRadioItem key={locale} value={locale} lang={locale} closeOnClick>
              {NATIVE_NAMES[locale]}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </Menu>
      {error && (
        <p role="alert" className={styles.error}>
          {_(msg`No se pudo guardar el idioma`)}
        </p>
      )}
    </div>
  );
}
